import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMapEvents,
} from "react-leaflet";
import { useGetStoresLocations } from "../../hooks/useProducts";

const DEFAULT_POSITION = [-1.2921, 36.8219];

function LocationPicker({ setPosition }) {
  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      // console.log("Picked:", lat, lng);
      setPosition([lat, lng]);
    },
  });

  return null;
}

function GeolocationMap({
  isMapShown,
  onOpenMapChange,
  handleLocationChange,
  onClose,
}) {
  const [position, setPosition] = useState(DEFAULT_POSITION);

  const handleChange = (coords) => {
    setPosition(coords);
    // Send to Parent
    handleLocationChange?.({
      latitude: coords[0],
      longitude: coords[1],
    });
  };

  return (
    <React.Fragment>
      {/* <Modal show={open} onHide={onOpenChange} centered> */}
      <Modal
        show={isMapShown}
        onHide={onOpenMapChange}
        centered
        size="xl"
        backdrop={"static"}
      >
        <Modal.Body>
          <div className="mapContainer">
            <MapContainer
              whenReady={(map) => map.target.invalidateSize()}
              // key={Date.now()}
              center={position}
              zoom={13}
              scrollWheelZoom={true}
              style={{ height: "400px", width: "100%" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <LocationPicker setPosition={handleChange} />

              <Marker position={position}>
                <Popup>
                  Selected: <br />
                  {position[0]}, {position[1]}
                </Popup>
              </Marker>
            </MapContainer>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-end">
            <button className="btn btn-sm btn-primary" onClick={onClose}>
              Close
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
}

export default GeolocationMap;

// function FitBounds({ locations }) {
//   const map = useMap();

//   useEffect(() => {
//     const bounds = L.latLngBounds(locations);
//     map.fitBounds(bounds);
//   }, [locations]);

//   return null;
// }

export const StoreLocations = ({ isShowStores, handleShowStores }) => {
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [geolocation, setLocation] = useState({
    userLat: null,
    userLng: null,
  });

  const {
    data = {},
    isPending,
    isError: storeIsError,
    error: storeError,
  } = useGetStoresLocations({ ...geolocation });

  let { stores = [] } = data;

  let locations = stores.map((item) => [item.latitude, item.longitude]) ?? [];

  const getCurrentLocation = () => {
    if (!("geolocation" in navigator)) {
      alert("Geolocation is not supported by your browser");
      return;
    }
    setIsLoadingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          userLat: position.coords.latitude,
          userLng: position.coords.longitude,
        });
        setIsLoadingLocation(false);
      },
      (error) => {
        switch (error.code) {
          case 1:
            // Permission denied
            alert(
              "Location access is blocked. Please enable it in your browser settings.",
            );
            break;

          case 2:
            alert(
              "Unable to detect your location. Try again or select manually.",
            );
            break;

          case 3:
            alert("Location request timed out. Please try again.");
            break;

          default:
            alert("An unknown error occurred while fetching location.");
        }
        setIsLoadingLocation(false);
        // toast.error("Please enable location access in your browser settings");
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 10000,
      },
    );
  };

  if (storeIsError)
    return (
      <Modal
        show={isShowStores}
        onHide={handleShowStores}
        centered
        size="xl"
        backdrop={"static"}
        // scrollable
      >
        <Modal.Body>
          <h6 className="text-center">Error getting stores</h6>
          <p className="text-danger text-center">{storeError}</p>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-end">
            <button
              className="btn btn-sm btn-primary"
              onClick={handleShowStores}
            >
              Close
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    );
  // return <p className="text-danger text-center">{storeError}</p>;

  return (
    <React.Fragment>
      <Modal
        show={isShowStores}
        onHide={handleShowStores}
        centered
        size="xl"
        backdrop={"static"}
        // scrollable
      >
        <Modal.Body>
          <div className="container-fluid">
            <div className="mb-10">
              <h6 className="fw-bold m-0">Find our Store Near You</h6>
              <p className="fs-6">
                Locate our stores and get directions, opening hours, and contact
                info.
              </p>
            </div>
            <div className="row">
              <div className="col-md-5">
                <form className="d-flex gap-10">
                  <div className="flex-grow-1 align-content-center">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Search location..."
                      disabled
                    />
                  </div>
                  <div className="">
                    {geolocation.userLat || geolocation.userLng ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        disabled={isLoadingLocation || isPending}
                        onClick={() =>
                          setLocation({
                            userLat: null,
                            userLng: null,
                          })
                        }
                      >
                        {(isLoadingLocation || isPending) && (
                          <div
                            className="spinner-border spinner-border-sm me-5"
                            role="status"
                          ></div>
                        )}
                        Clear Location
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={isLoadingLocation || isPending}
                        onClick={getCurrentLocation}
                      >
                        {(isLoadingLocation || isPending) && (
                          <div
                            className="spinner-border spinner-border-sm me-5"
                            role="status"
                          ></div>
                        )}
                        Current Location
                      </button>
                    )}
                  </div>
                </form>
                <hr />
                <ul className="list-group list-group-flush location_list">
                  {stores.map((store, index) => (
                    <li
                      className="list-group-item d-flex flex-column gap-10"
                      key={index}
                    >
                      <p className="fw-bold">{store.name}</p>
                      <p className="m-0 d-flex">
                        <i className="ph ph-phone me-5"></i>

                        <span>{store.phone}</span>
                      </p>
                      <p className="m-0 d-flex">
                        <i className="ph ph-at me-5"></i>
                        <span>{store.phone}</span>
                      </p>
                      <p className="m-0 d-flex">
                        <i className="ph ph-clock-countdown me-5"></i>
                        <span>{store.workingHours}</span>
                      </p>
                      {store?.distanceKm && (
                        <div className="d-flex justify-content-between">
                          <span className="fw-bold">
                            {Number(store?.distanceKm).toFixed(1)} KM
                          </span>
                          <button className="btn btn-sm bg-main-500 hover-bg-main-600 hover-text-white">
                            Get Directions
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="col-md">
                <MapContainer
                  whenReady={(map) => map.target.invalidateSize()}
                  center={[-1.2921, 36.8219]}
                  zoom={6}
                  scrollWheelZoom={true}
                  style={{ height: "400px", maxHeight: "100%", width: "100%" }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />

                  {locations.map((coords, index) => (
                    <Marker key={index} position={coords}>
                      <Popup>
                        Location <br />
                        {coords[0]}, {coords[1]}
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="d-flex justify-content-end">
            <button
              className="btn btn-sm btn-primary"
              onClick={handleShowStores}
              disabled={isLoadingLocation || isPending}
            >
              Close
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};
